import { useRef } from 'react';

import useUrl from '~/common/hooks/useUrl';

/**
 * @example ```jsx
    function ClickAuditHistory() {
      const { handleChangePageId } = useAuditHistoryWindow(); // useAuditHistoryWindow('summary/location/') will use /1/1/audit-history/summary/location/id as the path
      const handleClick = (e) => {
        handleChangePageId(e.target.value);
      };
    
      return (
        <>
          <Box>current ruleId: {ruleId}</Box>
          <Button value="250" onClick={handleClick}>
            Open Audit History ruleId 250
          </Button>
          <Button
            sx={{ marginLeft: '1rem' }}
            isPrimary={false}
            value="/location/9000"
            onClick={handleClick}
          >
            Open Audit History at location/9000
          </Button>
        </>
      );
    }
```
 */
export function useAuditHistoryWindow(auditHistoryLocation = '') {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
    obfuscatedLoanIdentifier || {};
  const windowRef = useRef<WindowProxy>(null);
  const customLocation = auditHistoryLocation.replace(
    /(?:^.+audit-history\/?)|(?:^[/\s])|(?:[/\s]$)/i,
    '',
  );
  const basePath = `/${obfuscatedLenderDatabaseId}/${obfuscatedLoanRecordId}/audit-history${customLocation ? '/' + customLocation + '/' : '/'}`;

  function handleChangePageId(id: number | string) {
    if (!id) return;

    const targetPath = basePath + id;

    if (!windowRef.current || windowRef.current.closed) {
      windowRef.current = window.open(
        targetPath,
        'window',
        `popup,width=1000,height=800`,
      );
      return;
    }

    const { pathname, search } = windowRef.current.location;
    // Assigning href (not pathname) keeps the query string, which pathname would percent-encode.
    if (pathname + search !== targetPath) {
      windowRef.current.location.href = targetPath;
    }
    windowRef.current.focus();
  }

  return { handleChangePageId };
}
